import type { ChartBindingValue } from '@/entities/dashboards';
import { createStore } from '@/shared/lib/store';

/** Выбранная ячейка heatmap для drill-down. `null` — drill не активен. */
export type DashboardDrill = { consultant: string; period: string } | null;

/**
 * Транзиентное «состояние рабочего места» дашборда качества: выбранная ячейка
 * тепловой карты (drill), выбранный консультант мини-отчёта, открытый звонок,
 * диапазон баллов drill-down и позиция вертикальной прокрутки страницы.
 *
 * ## Зачем отдельный стор
 * Уход в карточку диалога размонтирует страницу дашборда, а возврат монтирует её
 * заново — локальный `useState` при этом теряется, и разбор сбрасывается. Стор
 * держит это состояние в памяти, поэтому разбор и позиция прокрутки переживают
 * уход в диалог и возврат в рамках сессии.
 *
 * ## Чем отличается от фильтров
 * Фильтры панели персистятся за пользователем (localStorage + бэк) и переживают
 * перезагрузку. Состояние разбора — часть возврата к рабочему месту, а не
 * постоянная настройка: стор живёт только в памяти (без localStorage и
 * синхронизации на бэк), поэтому полный перезапуск страницы его сбрасывает —
 * этого от него и не требуется.
 */
export type DashboardScreenState = {
  /** Выбранная ячейка heatmap (консультант × период) */
  drill: DashboardDrill;
  /** Нижняя граница фильтра баллов drill-down */
  drillScoreFrom: string;
  /** Верхняя граница фильтра баллов drill-down */
  drillScoreTo: string;
  /** ID открытого звонка (карточка оценки CallDetailsCard); `null` — не открыт */
  selectedCallId: number | null;
  /** ФИО консультанта мини-отчёта; пустая строка — консультант не выбран */
  reportConsultant: string;
  /** Позиция вертикальной прокрутки дашборда */
  scrollTop: number;
  /**
   * Выбранные разрезы диаграмм пользовательского дашборда по имени разреза. Ключ — имя, а не
   * диаграмма: разрез одного имени общий для всех диаграмм, которые им разрезаны, и смена
   * значения перенаполняет их разом.
   */
  chartSlices: Record<string, ChartBindingValue>;
  /**
   * Выбранное в диаграмме по её идентификатору — им наполняются связанные с ней диаграммы.
   * Отсутствие записи и есть «ничего не выбрано»: связанная диаграмма тогда показывает подсказку.
   */
  chartSelections: Record<string, ChartBindingValue>;
  /** Задать значение разреза — перенаполняются все диаграммы, разрезанные разрезом этого имени */
  setChartSlice: (name: string, value: ChartBindingValue) => void;
  /** Выбрать элемент диаграммы; повторный выбор того же элемента снимает выбор */
  toggleChartSelection: (chartId: string, value: ChartBindingValue) => void;
  /** Выбрать ячейку heatmap; сбрасывает открытый звонок (как при обычном drill) */
  selectCell: (consultant: string, period: string) => void;
  /** Снять drill целиком: ячейка и открытый звонок */
  clearDrill: () => void;
  setDrillScoreFrom: (v: string) => void;
  setDrillScoreTo: (v: string) => void;
  /** Сбросить диапазон баллов drill-down к пустому */
  resetDrillScore: () => void;
  setSelectedCallId: (v: number | null) => void;
  setReportConsultant: (v: string) => void;
  setScrollTop: (v: number) => void;
};

/**
 * Стор транзиентного состояния экрана дашборда качества (в памяти, без
 * персиста). Прокрутку читает и пишет {@link useDashboardScrollRestore}; разбор —
 * страница дашборда через {@link useDashboardScreenState}.
 */
export const useDashboardScreenStore = createStore<DashboardScreenState>('dashboard-quality-screen', (set) => ({
  drill: null,
  drillScoreFrom: '',
  drillScoreTo: '',
  selectedCallId: null,
  reportConsultant: '',
  scrollTop: 0,
  chartSlices: {},
  chartSelections: {},
  setChartSlice: (name, value) =>
    set((s) => {
      s.chartSlices[name] = value;
    }),
  toggleChartSelection: (chartId, value) =>
    set((s) => {
      // Снятие выбора возвращает связанные диаграммы к подсказке — как будто в ведущей ещё
      // ничего не выбирали, поэтому запись убирается целиком, а не обнуляется значением.
      if (s.chartSelections[chartId] === value) delete s.chartSelections[chartId];
      else s.chartSelections[chartId] = value;
    }),
  selectCell: (consultant, period) =>
    set((s) => {
      s.drill = { consultant, period };
      s.selectedCallId = null;
    }),
  clearDrill: () =>
    set((s) => {
      s.drill = null;
      s.selectedCallId = null;
    }),
  setDrillScoreFrom: (v) =>
    set((s) => {
      s.drillScoreFrom = v;
    }),
  setDrillScoreTo: (v) =>
    set((s) => {
      s.drillScoreTo = v;
    }),
  resetDrillScore: () =>
    set((s) => {
      s.drillScoreFrom = '';
      s.drillScoreTo = '';
    }),
  setSelectedCallId: (v) =>
    set((s) => {
      s.selectedCallId = v;
    }),
  setReportConsultant: (v) =>
    set((s) => {
      s.reportConsultant = v;
    }),
  setScrollTop: (v) =>
    set((s) => {
      s.scrollTop = v;
    }),
}));

/**
 * Доступ страницы дашборда к состоянию разбора и его экшенам. Прокрутку
 * (`scrollTop`) намеренно не выбирает, чтобы её обновление при скролле не
 * перерисовывало страницу — за прокрутку отвечает {@link useDashboardScrollRestore}.
 */
export const useDashboardScreenState = () => ({
  drill: useDashboardScreenStore((s) => s.drill),
  drillScoreFrom: useDashboardScreenStore((s) => s.drillScoreFrom),
  drillScoreTo: useDashboardScreenStore((s) => s.drillScoreTo),
  selectedCallId: useDashboardScreenStore((s) => s.selectedCallId),
  reportConsultant: useDashboardScreenStore((s) => s.reportConsultant),
  selectCell: useDashboardScreenStore((s) => s.selectCell),
  clearDrill: useDashboardScreenStore((s) => s.clearDrill),
  setDrillScoreFrom: useDashboardScreenStore((s) => s.setDrillScoreFrom),
  setDrillScoreTo: useDashboardScreenStore((s) => s.setDrillScoreTo),
  resetDrillScore: useDashboardScreenStore((s) => s.resetDrillScore),
  setSelectedCallId: useDashboardScreenStore((s) => s.setSelectedCallId),
  setReportConsultant: useDashboardScreenStore((s) => s.setReportConsultant),
});

/**
 * Разрезы и выбранное в диаграммах пользовательского дашборда.
 *
 * Живут в том же транзиентном сторе, что и прочий разбор: выбор пользователя переживает уход в
 * диалог и возврат, но состав дашборда не меняет и на бэкенд не уходит.
 */
export const useDashboardChartBindings = () => ({
  chartSlices: useDashboardScreenStore((s) => s.chartSlices),
  chartSelections: useDashboardScreenStore((s) => s.chartSelections),
  setChartSlice: useDashboardScreenStore((s) => s.setChartSlice),
  toggleChartSelection: useDashboardScreenStore((s) => s.toggleChartSelection),
});
